import { DEFAULT_LOCALE } from "../money";
import { catalogueChain } from "./catalogue";
import type { Catalogue } from "./catalogue";
import { isDevelopment } from "./dev-log";
import { interpolate } from "./interpolate";
import type { WordParams } from "./interpolate";

export const NEUTRAL_KEY = "wording.neutral";
const BUILT_IN_NEUTRAL = "Something is not available right now.";

export type WordOptions = {
  /** Wording code to use when `code` has no words (for example `refusal.generic`). */
  readonly fallbackCode?: string;
};

export type WordingProblem = { readonly code: string; readonly reason: "MISSING" | "PARAMS" };

export type Wording = {
  word(code: string, params?: WordParams, locale?: string, options?: WordOptions): string;
  has(code: string, locale?: string): boolean;
};

type Lookup = { readonly text: string } | { readonly reason: WordingProblem["reason"] };

type CreateWordingOptions = {
  readonly catalogue: Catalogue;
  /** Called once per code that had no usable words. Defaults to a console warning in development. */
  readonly onProblem?: (problem: WordingProblem) => void;
};

function warnInDevelopment(problem: WordingProblem): void {
  if (isDevelopment()) console.warn(`[wording] no usable words for "${problem.code}" (${problem.reason})`);
}

/**
 * the console maps codes to words. A code with no words (a newer server) never reaches the
 * user raw: they get a neutral sentence, and developers get one warning.
 */
export function createWording({ catalogue, onProblem = warnInDevelopment }: CreateWordingOptions): Wording {
  const reported = new Set<string>();

  function template(code: string, locale: string): string | undefined {
    for (const messages of catalogueChain(catalogue, locale)) {
      const found = messages[code];
      if (found !== undefined) return found;
    }
    return undefined;
  }

  function report(problem: WordingProblem): void {
    if (reported.has(problem.code)) return;
    reported.add(problem.code);
    onProblem(problem);
  }

  function lookup(code: string, params: WordParams, locale: string): Lookup {
    const found = template(code, locale);
    if (found === undefined) return { reason: "MISSING" };
    const text = interpolate(found, params, locale);
    return text === undefined ? { reason: "PARAMS" } : { text };
  }

  function word(code: string, params: WordParams = {}, locale: string = DEFAULT_LOCALE, options: WordOptions = {}): string {
    const first = lookup(code, params, locale);
    if ("text" in first) return first.text;
    report({ code, reason: first.reason });
    const fallback = options.fallbackCode ? lookup(options.fallbackCode, params, locale) : undefined;
    if (fallback && "text" in fallback) return fallback.text;
    return template(NEUTRAL_KEY, locale) ?? BUILT_IN_NEUTRAL;
  }

  return { word, has: (code, locale = DEFAULT_LOCALE) => template(code, locale) !== undefined };
}
